import { useState, useCallback, useEffect, useRef } from 'react';
import {
  exportSlideToBlob,
  exportSlideToJpegBlob,
  exportSlideV2,
  getExportData,
  startVideoExportWithProgress,
  startVideoExportV2,
  computeVideoRect,
  VideoExportStage,
} from '../utils/exportApi';

/**
 * Export types
 */
export const ExportType = {
  PNG: 'png',
  JPEG: 'jpeg',
  VIDEO: 'video',
};

/**
 * Export status states
 */
export const ExportStatus = {
  IDLE: 'idle',
  EXPORTING: 'exporting',
  COMPLETE: 'complete',
  ERROR: 'error',
};

/**
 * Initial export state
 */
const initialExportState = {
  type: null,       // 'png' | 'jpeg' | 'video' | null
  status: 'idle',   // 'idle' | 'exporting' | 'complete' | 'error'
  url: null,        // Object URL of exported file
  progress: null,   // Video progress data (for video exports)
  error: null,      // Error message if failed
};

/**
 * Custom hook for export functionality
 * Handles PNG, JPEG, and Video exports with unified state machine
 * 
 * @param {React.RefObject} previewRef - Reference to preview panel for export node
 * @param {number} activeIndex - Current active slide index
 * @param {Object} activeSlide - Current active slide data
 * @returns {Object} Export state and handlers
 */
export function useExport(previewRef, activeIndex, activeSlide, carouselId = null, resolveErrors = {}) {
  const [exportState, setExportState] = useState(initialExportState);

  // ── Phase 0: Fetch feature flags for execution routing ─────────────
  const flagsRef = useRef({
    execution_mode: 'worker_primary',
    allow_silent_fallback: false,
    legacy_export: false,
    legacy_image_export: true,
  });

  const refreshFlags = useCallback(async () => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    try {
      const r = await fetch(`${backendUrl}/api/config/flags`);
      const data = await r.json();
      flagsRef.current = {
        execution_mode: data.execution_mode || 'worker_primary',
        allow_silent_fallback: data.allow_silent_fallback || false,
        legacy_export: data.legacy_export || false,
        legacy_image_export: data.legacy_image_export !== undefined ? data.legacy_image_export : true,
      };
      return flagsRef.current;
    } catch {
      return flagsRef.current;
    }
  }, []);

  useEffect(() => {
    refreshFlags().then(flags => {
      console.log('[useExport] Feature flags loaded:', flags);
    });
  }, [refreshFlags]);

  /**
   * Trigger file download
   */
  const triggerDownload = useCallback((objectUrl, filename) => {
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    link.remove();
  }, []);

  /**
   * Start export (set exporting state)
   */
  const startExport = useCallback((type) => {
    setExportState({
      type,
      status: ExportStatus.EXPORTING,
      url: null,
      progress: type === ExportType.VIDEO 
        ? { stage: VideoExportStage.PREPARING, progress_percent: 0 } 
        : null,
      error: null,
    });
  }, []);

  /**
   * Complete export (set complete state with URL)
   */
  const completeExport = useCallback((url) => {
    setExportState(prev => ({
      ...prev,
      status: ExportStatus.COMPLETE,
      url,
      progress: null,
    }));
  }, []);

  /**
   * Fail export (set error state)
   */
  const failExport = useCallback((error) => {
    setExportState(prev => ({
      ...prev,
      status: ExportStatus.ERROR,
      error: error.message || 'Export failed',
      progress: null,
    }));
  }, []);

  /**
   * Update video progress
   */
  const updateProgress = useCallback((progress) => {
    // Debug: Log progress updates received by hook
    if (progress.stage === 'compositing' || progress.stage === 'preparing_video') {
      console.log('[useExport] Progress update:', {
        stage: progress.stage,
        step: progress.step_number,
        encoded_time: progress.encoded_time,
        proxy_needed: progress.proxy_needed,
      });
    }
    setExportState(prev => ({
      ...prev,
      progress,
    }));
  }, []);

  /**
   * Reset export state (close modal)
   */
  const resetExport = useCallback(() => {
    setExportState(initialExportState);
  }, []);

  /**
   * Validate that an export blob is non-trivial.
   * A blank/broken 1080×1350 export is typically < 5KB.
   * A real slide with content is typically > 20KB.
   */
  const validateExportBlob = useCallback((blob, format) => {
    // Minimum expected sizes (bytes) for a 1080×1350 slide with content
    const MIN_SIZES = { png: 5000, jpeg: 3000 };
    const minSize = MIN_SIZES[format] || 3000;

    if (!blob || blob.size === 0) {
      throw new Error(
        'Export produced an empty file. This usually means asset URLs failed to resolve. ' +
        'Please reload the page and try again.'
      );
    }
    if (blob.size < minSize) {
      console.warn(
        `[Export] Suspiciously small ${format.toUpperCase()} blob: ${blob.size} bytes ` +
        `(expected > ${minSize}). Export may be blank or missing assets.`
      );
      // Don't throw — just warn. Some slides legitimately produce small files
      // (e.g., solid color background with minimal text).
    }
  }, []);

  /**
   * Check if the current slide has transient asset resolution failures
   * that would produce a blank export.
   *
   * BLOCKING (transient — retry/refresh may fix):
   *   - resolveErrors with 'server_error' or 'resolve_error'
   *     These mean the backend was temporarily unreachable.
   *
   * NON-BLOCKING (permanent — asset is gone, renderer uses fallback):
   *   - resolveErrors with 'not_found', 'deleted', 'unconfirmed'
   *     These are permanent states.  The slide renderer gracefully
   *     skips missing assets and uses fallback backgrounds/colors.
   *     Blocking here would make slides with stale asset refs
   *     permanently unexportable.
   *
   * @param {'image'|'video'} exportKind - 'image' for PNG/JPEG, 'video' for MP4
   * @returns {string|null} Error message if transient failures, null if OK
   */
  const checkSlideAssetsResolved = useCallback((exportKind = 'image') => {
    if (!activeSlide) return null;

    // Only block on transient errors that a refresh might fix
    const TRANSIENT_REASONS = new Set(['server_error', 'resolve_error']);

    const transientFailures = [];
    const permanentWarnings = [];

    // Collect all asset_ids referenced by this slide
    const assetIds = [];
    if (activeSlide.bgAssetId) assetIds.push({ id: activeSlide.bgAssetId, label: 'background' });
    if (activeSlide.imageWidget?.asset_id) assetIds.push({ id: activeSlide.imageWidget.asset_id, label: 'image widget' });
    if (activeSlide.videoWidget?.asset_id && exportKind === 'video') {
      assetIds.push({ id: activeSlide.videoWidget.asset_id, label: 'video widget' });
    }

    for (const { id, label } of assetIds) {
      const reason = resolveErrors[id];
      if (!reason) continue;  // No error — resolved OK or not attempted

      if (TRANSIENT_REASONS.has(reason)) {
        transientFailures.push(`${label}: ${reason}`);
      } else {
        permanentWarnings.push(`${label}: ${reason}`);
      }
    }

    // Log permanent warnings but do NOT block export
    if (permanentWarnings.length > 0) {
      console.warn(
        '[Export] Slide has permanently missing assets (will use fallback):',
        permanentWarnings.join(', ')
      );
    }

    // Block only on transient failures
    if (transientFailures.length > 0) {
      console.error('[Export] Blocked: transient asset resolution failures:', transientFailures);
      return (
        'Export blocked: media service temporarily unavailable.\n' +
        'Unresolved: ' + transientFailures.join(', ') + '\n' +
        'Please refresh the page and try again.'
      );
    }

    return null;
  }, [activeSlide, resolveErrors]);

  /**
   * Export to PNG using backend Playwright renderer.
   * Phase 5: Uses worker-first v2 export when legacy_image_export=false.
   */
  const handleExport = useCallback(async () => {
    const exportNode = previewRef.current?.getExportNode();
    if (!exportNode) return;

    // ── Pre-export: check asset hydration ──────────────────────────
    const assetError = checkSlideAssetsResolved('image');
    if (assetError) {
      failExport(new Error(assetError));
      return;
    }

    try {
      startExport(ExportType.PNG);

      const { html, css } = getExportData(exportNode);
      const flags = flagsRef.current;
      let blob;

      if (!flags.legacy_image_export && flags.execution_mode !== 'render_emergency') {
        // Phase 5: Worker-first PNG export — NO legacy fallback
        // Legacy image export is intentionally disabled (410 Gone).
        // On v2 failure, surface the error directly — do NOT call exportSlideToBlob.
        console.log('[Export] PNG export started');
        blob = await exportSlideV2(html, css, {
          format: 'png',
          width: 1080,
          height: 1350,
          scale: 2.0,
          transparent: true,
          carouselId: carouselId || null,
          slideId: activeSlide?.id || null,
        });
      } else {
        // Legacy: direct backend Playwright rendering (render_emergency only)
        blob = await exportSlideToBlob(html, css, {
          width: 1080,
          height: 1350,
          scale: 2.0,
        });
      }

      validateExportBlob(blob, 'png');
      const objectUrl = URL.createObjectURL(blob);
      completeExport(objectUrl);
      triggerDownload(objectUrl, `magic-forest-slide-${activeIndex + 1}.png`);

    } catch (e) {
      console.error('[Export] PNG export failed:', e);
      failExport(e);
    }
  }, [activeIndex, activeSlide, carouselId, previewRef, triggerDownload, startExport, completeExport, failExport, validateExportBlob, checkSlideAssetsResolved]);

  /**
   * Export to JPEG using backend Playwright renderer (Instagram-optimized).
   * Phase 5: Uses worker-first v2 export when legacy_image_export=false.
   */
  const handleExportJpeg = useCallback(async () => {
    const exportNode = previewRef.current?.getExportNode();
    if (!exportNode) return;

    // ── Pre-export: check asset hydration ──────────────────────────
    const assetError = checkSlideAssetsResolved('image');
    if (assetError) {
      failExport(new Error(assetError));
      return;
    }

    try {
      startExport(ExportType.JPEG);

      const { html, css } = getExportData(exportNode);
      const flags = flagsRef.current;
      let blob;

      if (!flags.legacy_image_export && flags.execution_mode !== 'render_emergency') {
        // Phase 5: Worker-first JPEG export — NO legacy fallback
        // Legacy image export is intentionally disabled (410 Gone).
        // On v2 failure, surface the error directly — do NOT call exportSlideToJpegBlob.
        console.log('[Export] JPEG export started');
        blob = await exportSlideV2(html, css, {
          format: 'jpeg',
          width: 1080,
          height: 1350,
          scale: 1.0,
          quality: 85,
          carouselId: carouselId || null,
          slideId: activeSlide?.id || null,
        });
      } else {
        // Legacy: direct backend Playwright rendering (render_emergency only)
        blob = await exportSlideToJpegBlob(html, css, {
          width: 1080,
          height: 1350,
          quality: 85,
        });
      }

      validateExportBlob(blob, 'jpeg');
      const objectUrl = URL.createObjectURL(blob);
      completeExport(objectUrl);
      triggerDownload(objectUrl, `magic-forest-slide-${activeIndex + 1}.jpg`);

    } catch (e) {
      console.error('[Export] JPEG export failed:', e);
      failExport(e);
    }
  }, [activeIndex, activeSlide, carouselId, previewRef, triggerDownload, startExport, completeExport, failExport, validateExportBlob, checkSlideAssetsResolved]);

  /**
   * Export to video using backend renderer with progress tracking.
   *
   * Phase 3 (Migration Plan): Worker-first video export.
   * - render_emergency mode → use legacy in-process export directly
   * - worker_primary / worker_required → use v2 worker export
   * - NEVER silently fall from v2 to legacy (DEC-004)
   * - If v2 returns 409 (mode changed to emergency), refresh flags and retry once
   * - If v2 returns 503 (workers down), surface clear error
   */
  const handleExportVideo = useCallback(async () => {
    const exportNode = previewRef.current?.getExportNode();
    if (!exportNode) return;
    
    const videoDuration = activeSlide?.videoWidget?.duration;
    if (!videoDuration) {
      failExport(new Error('No video duration found. Please upload a video first.'));
      return;
    }

    // ── Pre-export: check asset hydration (video needs video + bg) ──
    const assetError = checkSlideAssetsResolved('video');
    if (assetError) {
      failExport(new Error(assetError));
      return;
    }

    const assetId = activeSlide?.videoWidget?.asset_id;
    const flags = flagsRef.current;
    const mode = flags.execution_mode;

    try {
      startExport(ExportType.VIDEO);
      const { html, css } = getExportData(exportNode);

      let blob;

      // ── Routing decision based on execution mode ───────────────
      if (mode === 'render_emergency') {
        // Emergency mode: use legacy in-process export directly
        console.log('[Export] MP4 export started: render_emergency → legacy in-process');
        blob = await startVideoExportWithProgress(html, css, videoDuration, {
          width: 1080,
          height: 1350,
          onProgress: updateProgress,
          forceLegacy: true,  // Phase 3: Emergency mode uses legacy explicitly
        });
      } else if (assetId) {
        // worker_primary or worker_required: use v2 worker export
        console.log(`[Export] MP4 export started: ${mode} → v2 worker, asset=${assetId}`);
        const videoRect = computeVideoRect(exportNode);
        if (!videoRect) {
          throw new Error('Could not determine video position in slide');
        }

        try {
          blob = await startVideoExportV2(assetId, html, css, videoRect, {
            onProgress: updateProgress,
            carouselId: carouselId || null,
            slideId: activeSlide?.id || null,
          });
        } catch (v2Error) {
          // Phase 3: Handle specific v2 error types
          if (v2Error.errorType === 'emergency_mode' && v2Error.useLegacy) {
            // Server switched to emergency mode — refresh flags and retry with legacy
            console.log('[Export] Server in emergency mode — refreshing flags, retrying with legacy');
            await refreshFlags();
            updateProgress({
              stage: 'preparing',
              progress_percent: 5,
              stage_message: 'Switched to local rendering...',
            });
            blob = await startVideoExportWithProgress(html, css, videoDuration, {
              width: 1080,
              height: 1350,
              onProgress: updateProgress,
              forceLegacy: true,  // Phase 3: Emergency mode — backend allows via force flag
            });
          } else if (v2Error.errorType === 'workers_unavailable') {
            throw new Error(
              'Video rendering workers are temporarily unavailable. ' +
              'Please try again in a few minutes.'
            );
          } else if (v2Error.errorType === 'invalid_asset') {
            throw new Error(
              'The video source could not be found. ' +
              'Please remove and re-upload the video, then try again.'
            );
          } else {
            // Unknown v2 error — propagate with context
            throw v2Error;
          }
        }
      } else {
        // No asset_id — video was uploaded via legacy flow (no S3 asset)
        if (mode === 'worker_required') {
          throw new Error(
            'This video was uploaded before cloud storage was enabled. ' +
            'Please re-upload the video to use worker-based export.'
          );
        }
        // worker_primary with no asset_id: use legacy as the only option
        // (this is not a "silent fallback" — it's the only available path for legacy uploads)
        console.log('[Export] MP4 export started: legacy upload → legacy in-process');
        blob = await startVideoExportWithProgress(html, css, videoDuration, {
          width: 1080,
          height: 1350,
          onProgress: updateProgress,
          forceLegacy: true,  // Phase 3: Tell backend this is legacy-upload-only (no alternative)
        });
      }

      const objectUrl = URL.createObjectURL(blob);
      completeExport(objectUrl);
      triggerDownload(objectUrl, `magic-forest-slide-${activeIndex + 1}.mp4`);

    } catch (e) {
      console.error('[Export] MP4 export failed:', e);

      // Phase 3: User-friendly error messages based on error type
      let errorMessage = e.message || 'Video export failed. Please try again.';
      if (e.errorType === 'workers_unavailable') {
        errorMessage = 'Video rendering service is temporarily unavailable. Please try again in a few minutes.';
      } else if (e.errorType === 'invalid_asset') {
        errorMessage = 'Video source not found. Please remove and re-upload the video.';
      } else if (e.status === 410) {
        errorMessage = 'This export method is no longer available. Please refresh the page and try again.';
      }

      failExport(new Error(errorMessage));
    }
  }, [activeIndex, activeSlide, carouselId, previewRef, triggerDownload, startExport, completeExport, failExport, updateProgress, refreshFlags, checkSlideAssetsResolved]);

  // Derived state for backward compatibility and convenience
  const isExporting = exportState.status === ExportStatus.EXPORTING && exportState.type === ExportType.PNG;
  const isExportingJpeg = exportState.status === ExportStatus.EXPORTING && exportState.type === ExportType.JPEG;
  const isExportingVideo = exportState.status === ExportStatus.EXPORTING && exportState.type === ExportType.VIDEO;
  const isAnyExporting = exportState.status === ExportStatus.EXPORTING;

  // Export error — visible when status is 'error'
  const exportError = exportState.status === ExportStatus.ERROR ? exportState.error : null;

  // Auto-dismiss export error after 8 seconds
  useEffect(() => {
    if (!exportError) return;
    const timer = setTimeout(() => {
      setExportState(prev => {
        if (prev.status === ExportStatus.ERROR) return initialExportState;
        return prev;
      });
    }, 8000);
    return () => clearTimeout(timer);
  }, [exportError]);

  const dismissExportError = useCallback(() => {
    setExportState(prev => {
      if (prev.status === ExportStatus.ERROR) return initialExportState;
      return prev;
    });
  }, []);

  // URLs for modals (only available when complete)
  const exportUrl = exportState.type === ExportType.PNG && exportState.status === ExportStatus.COMPLETE 
    ? exportState.url : '';
  const jpegExportUrl = exportState.type === ExportType.JPEG && exportState.status === ExportStatus.COMPLETE 
    ? exportState.url : '';
  const videoExportUrl = exportState.type === ExportType.VIDEO && exportState.status === ExportStatus.COMPLETE 
    ? exportState.url : '';

  return {
    // Unified state
    exportState,
    
    // Derived boolean flags (backward compatible)
    isExporting,
    isExportingJpeg,
    isExportingVideo,
    isAnyExporting,

    // Export error (null when no error)
    exportError,
    dismissExportError,
    
    // URLs for modals
    exportUrl,
    jpegExportUrl,
    videoExportUrl,
    
    // Video progress
    videoExportProgress: exportState.progress,
    
    // Legacy compatibility
    exportSafe: false,
    
    // Handlers
    handleExport,
    handleExportJpeg,
    handleExportVideo,
    
    // Modal close handlers (all use resetExport now)
    clearExportUrl: resetExport,
    clearJpegExportUrl: resetExport,
    clearVideoExportUrl: resetExport,
    resetExport,
  };
}
